import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PMPagination } from "../../../utils/PMPagination";
import { Link, useNavigate } from "react-router-dom";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { 
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../contexts/settings-context";

export const CalculationsTable = (props) => {
  const {
    itemState,
    onPageChange,
    itemsCount,
    page,
    perPage,
    currentCount,
    onDelete,
    onViewVersions,
    versionsEnabled = false,
  } = props;
  const { data, isLoading, errors } = itemState;
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const navigate = useNavigate();

  const getCalculationTypeLabel = (type) => {
    const labels = {
      standard: t("offer.calculation.type.standard"),
      product: t("offer.calculation.type.product"),
      hours: t("offer.calculation.type.hours"),
      car_allowance: t("offer.calculation.type.car_allowance"),
      nested: t("offer.calculation.type.nested"),
    };
    return labels[type] || type;
  };

  const getCalculationTypeColor = (type) => {
    const colors = {
      standard: "bg-blue-100 text-blue-800",
      product: "bg-green-100 text-green-800",
      hours: "bg-purple-100 text-purple-800",
      car_allowance: "bg-orange-100 text-orange-800",
      nested: "bg-indigo-100 text-indigo-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const CalculationCard = ({ item }) => {
    const isActive = item.active !== false;
    const calculationType = item.calculation_type || "standard";
    
    return (
      <div 
        className="bg-white shadow-sm rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => navigate("/dashboard/offers/calculations/" + item.id)}
      >
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <DocumentTextIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {item.title || t("offer.calculation.untitled")}
                  </h3>
                </div>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getCalculationTypeColor(calculationType)}`}>
                  {getCalculationTypeLabel(calculationType)}
                </span>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  isActive 
                    ? "bg-green-100 text-green-800" 
                    : "bg-gray-100 text-gray-800"
                }`}>
                  {isActive ? t("Aktiv") : t("Inaktiv")}
                </span>
              </div>
              
              {item.description && (
                <p className="text-sm text-gray-500 mb-4 line-clamp-2 ml-13">
                  {item.description}
                </p>
              )}
              
              {item.total_amount !== undefined && (
                <div className="mt-4 ml-13">
                  <div className="text-xs text-gray-500 mb-1">
                    {t("Totalbeløp")}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {priceFormatter.format(item.total_amount || 0)}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              {versionsEnabled ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewVersions?.(item);
                  }}
                  className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  title={t("offers.calculation.versions.title")}
                >
                  <ClockIcon className="h-5 w-5" />
                </button>
              ) : null}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/dashboard/offers/calculations/" + item.id);
                }}
                className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                title={t("Rediger")}
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDelete) {
                    onDelete(item, e);
                  }
                }}
                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title={t("Slett")}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (errors) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="text-center text-red-600">
          {t("offer.calculation.load_error")}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <ResourceEmpty message={t("offer.calculation.no_results")} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {data.map((item) => (
          <CalculationCard key={item.id} item={item} />
        ))}
      </div>
      
      {itemsCount > 0 && (
        <div className="bg-white px-6 py-4 border-t border-gray-200 rounded-b-lg">
          <PMPagination
            totalCount={itemsCount}
            currentCount={currentCount}
            page={page}
            perPage={perPage}
            onPageChange={onPageChange}
            onPerPageChange={(newPerPage) => {
              onPageChange(1);
              // Note: perPage change would need to be handled by parent
            }}
          />
        </div>
      )}
    </div>
  );
};

CalculationsTable.defaultProps = {
  itemsCount: 0,
  currentCount: 0,
  page: 1,
  perPage: 10,
  onPageChange: () => {},
  onDelete: () => {},
};

